import {
  CSSProperties,
  forwardRef,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom';

import { cx } from '@libs/classnames';
import { FUNC_STOP_PROPAGATION } from '@libs/constants';
import { useClickOutsideAlt } from '@libs/hooks/useClickOutside';
import { useDynamicFontScaling } from '@libs/hooks/useDynamicFontScaling';
import { useFormatNumber } from '@libs/hooks/useFormatNumber';
import { useMultipleHover } from '@libs/hooks/useMultipleHover';
import { AnimatedNode } from '@ui/components/Animation';
import { Avatar } from '@ui/components/Avatar';
import { useMediaQuery } from '@providers/MediaQueryProvider';
import { ProfileLinkIcon } from '@ui/icons/ProfileLinkIcon/ProfileLinkIcon';
import { ShieldIconDrawn } from '@ui/icons/shields/ShieldIconDrawn';

import { PlayerStats as PlayerStatsType } from '@features/shared/api/typings';
import {
  EFFECTIVENESS_DESCRIPTION,
  EXPERIENCE_DESCRIPTION,
  RANK_DESCRIPTION,
  STAMINA_DESCRIPTION,
  TACTICS_DESCRIPTION,
  TACTICS_DESCRIPTION_FOR_WIDGET,
  TOTAL_RANK_DESCRIPTION,
} from '@features/user/ui/PlayerShieldAvatar/descriptions';

import styles from './PlayerShieldAvatar.module.css';

const SHADOW_AVATAR = __RUNTIME.baseUrl + '/stubs/shadow.png';

type Props = {
  size: 'm' | 'l';
  className?: string;
  style?: CSSProperties;
  stats?: PlayerStatsType;
  popoverDescriptionsEnabled?: boolean;
  goToPlayerProfile?: () => void;
  onMouseLeave: () => void;
  onMouseEnter: () => void;
  isWidget?: boolean;
};
export const PlayerShieldAvatar = forwardRef<HTMLDivElement, Props>(
  function PlayerShieldAvatarInner(
    {
      size,
      className,
      style,
      stats,
      popoverDescriptionsEnabled,
      goToPlayerProfile,
      onMouseLeave,
      onMouseEnter,
      isWidget = false,
    }: Props,
    ref,
  ) {
    const stopPropagation = useCallback((event: MouseEvent) => {
      event.stopPropagation();
    }, []);

    if (!stats) return null;

    return (
      <div
        ref={ref}
        className={cx(
          styles.shieldContainer,
          { [styles.size_m]: size === 'm', [styles.size_l]: size === 'l' },
          className,
        )}
        style={style}
        onMouseLeave={onMouseLeave}
        onMouseEnter={onMouseEnter}
        onClick={stopPropagation}
        onPointerDown={stopPropagation}
      >
        <ShieldIconDrawn className={styles.shield} shieldNumber={2} />

        <div className={styles.content}>
          <Avatar
            className={styles.avatar}
            text=""
            size={size === 'l' ? '200' : '108'}
            imageSrc={stats.avatarSrc || SHADOW_AVATAR}
          />

          <TotalRate stats={stats} popoverDescriptionsEnabled={popoverDescriptionsEnabled} />

          <PlayerName firstName={stats.firstName} lastName={stats.lastName} size={size} />

          <PlayerStats
            stats={stats}
            popoverDescriptionsEnabled={popoverDescriptionsEnabled}
            isWidget={isWidget}
          />

          {goToPlayerProfile && (
            <div className={styles.playerProfileButton} onClick={goToPlayerProfile}>
              <ProfileLinkIcon />
            </div>
          )}
        </div>
      </div>
    );
  },
);

type PlayerNameProps = {
  lastName: string;
  firstName: string;
  size: 'm' | 'l';
};
function PlayerName({ firstName, lastName, size }: PlayerNameProps) {
  const lastNameRef = useRef<HTMLDivElement>(null);
  const firstNameRef = useRef<HTMLDivElement>(null);
  const lastNameStyle = useDynamicFontScaling(lastNameRef, {
    maxHeight: size === 'm' ? 28 : 56,
  });
  const firstNameStyle = useDynamicFontScaling(firstNameRef, {
    maxHeight: size === 'm' ? 28 : 56,
  });

  let style = lastNameStyle;
  if (firstNameStyle.fontSize && (!style.fontSize || firstNameStyle.fontSize < style.fontSize))
    style = firstNameStyle;

  return (
    <div className={styles.name}>
      <div style={style} ref={lastNameRef}>
        {lastName}
      </div>
      <div style={style} ref={firstNameRef}>
        {firstName}
      </div>
    </div>
  );
}

type PlayerStatsProps = {
  stats?: PlayerStatsType;
  popoverDescriptionsEnabled?: boolean;
  isWidget?: boolean;
};
function PlayerStats({ stats, popoverDescriptionsEnabled, isWidget }: PlayerStatsProps) {
  return (
    <div
      className={cx(styles.stats, {
        [styles.clickable]: popoverDescriptionsEnabled,
      })}
    >
      <div className={styles.statsRow}>
        <PlayerStat
          title="Выносливость"
          label="Вын."
          value={useFormatNumber(stats?.stamina) ?? '??'}
          description={STAMINA_DESCRIPTION}
        />
        <PlayerStat
          title="Тактика"
          label="Такт."
          value={useFormatNumber(stats?.tactics) ?? '??'}
          description={isWidget ? TACTICS_DESCRIPTION_FOR_WIDGET : TACTICS_DESCRIPTION}
        />
        <PlayerStat
          title="Рейтинг"
          label="Рейт."
          value={useFormatNumber(stats?.rate) ?? '??'}
          description={RANK_DESCRIPTION}
        />
      </div>
      <div className={styles.statsRow}>
        <PlayerStat
          title="Опыт"
          label="Опыт"
          value={useFormatNumber(stats?.experience) ?? '??'}
          description={EXPERIENCE_DESCRIPTION}
        />
        <PlayerStat
          title="Эффективность"
          label="Эфф."
          value={useFormatNumber(stats?.efficiency) ?? '??'}
          description={EFFECTIVENESS_DESCRIPTION}
        />
      </div>
    </div>
  );
}

type PlayerStatProps = {
  title: string;
  label: ReactNode;
  value: ReactNode;
  description?: ReactNode;
};
function PlayerStat({ title, label, value, description }: PlayerStatProps) {
  const {
    descriptionVisible,
    toggleDescription,
    showDescription,
    hideDescription,
    outsideClickRef,
    anchorRef,
    setPopoverRef,
    popoverStyles,
  } = useDescriptionPopover();

  const { isHovered, onMouseEnter, onMouseLeave } = useMultipleHover();

  useEffect(() => {
    if (isHovered) showDescription();
    else hideDescription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovered]);

  return (
    <div
      className={styles.stat}
      onClick={toggleDescription}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={outsideClickRef}
    >
      <div className={styles.label}>{label}</div>
      <div className={styles.value} ref={anchorRef}>
        {value}
      </div>

      <DescriptionPopover
        ref={setPopoverRef}
        style={popoverStyles}
        title={title}
        description={description}
        isOpen={descriptionVisible}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    </div>
  );
}

type TotalRateProps = {
  stats?: PlayerStatsType;
  popoverDescriptionsEnabled?: boolean;
};
function TotalRate({ stats, popoverDescriptionsEnabled }: TotalRateProps) {
  const {
    descriptionVisible,
    popoverStyles,
    setPopoverRef,
    anchorRef,
    outsideClickRef,
    toggleDescription,
    showDescription,
    hideDescription,
  } = useDescriptionPopover();

  const { isHovered, onMouseEnter, onMouseLeave } = useMultipleHover();
  useEffect(() => {
    if (isHovered) showDescription();
    else hideDescription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovered]);

  return (
    <div
      className={cx(styles.totalRate, {
        [styles.clickable]: popoverDescriptionsEnabled,
      })}
      ref={outsideClickRef}
      onClick={toggleDescription}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={styles.rate} ref={anchorRef}>
        {stats?.totalRate}
      </div>

      <DescriptionPopover
        ref={setPopoverRef}
        title="Основной рейтинг"
        description={TOTAL_RANK_DESCRIPTION}
        isOpen={descriptionVisible}
        style={popoverStyles}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      />
    </div>
  );
}

type DescriptionPopoverProps = {
  title: string;
  description: ReactNode;
  isOpen: boolean;
  style: CSSProperties;
  onMouseEnter: (e: MouseEvent) => void;
  onMouseLeave: (e: MouseEvent) => void;
};
const DescriptionPopover = forwardRef<HTMLDivElement, DescriptionPopoverProps>(
  function DescriptionPopoverInner(
    { title, description, isOpen, style, onMouseLeave, onMouseEnter },
    ref,
  ) {
    const handleClick = useCallback((e: MouseEvent) => {
      // Адовый хак - почему-то на мобилках клик по нативной ссылке с первого раза не срабатывает.
      // Если выставить такой онклик, то работает.
      if (
        'tagName' in e.target &&
        'href' in e.target &&
        e.target.tagName === 'A' &&
        e.target.href
      ) {
        e.preventDefault();
        e.stopPropagation();
        if ('target' in e.target && e.target.target) {
          window.open(e.target.href as string, e.target.target as string);
        } else {
          window.location.href = e.target.href as string;
        }
      }
    }, []);

    return createPortal(
      <AnimatedNode initial>
        {description && isOpen && (
          <div
            className={styles.popover}
            ref={ref}
            style={style}
            onClick={FUNC_STOP_PROPAGATION}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <div>
              <div className={styles.title}>{title}</div>

              <div
                className={styles.description}
                onClick={handleClick}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </div>
          </div>
        )}
      </AnimatedNode>,
      document.body,
    );
  },
);

function useDescriptionPopover() {
  const isMobile = useMediaQuery() === 'mobile';
  const [descriptionVisible, setDescriptionVisible] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const toggleDescription = useCallback(() => {
    setDescriptionVisible(!descriptionVisible);
  }, [setDescriptionVisible, descriptionVisible]);

  const hideDescription = useCallback(() => {
    setDescriptionVisible(false);
  }, [setDescriptionVisible]);

  const showDescription = useCallback(() => {
    setDescriptionVisible(true);
  }, [setDescriptionVisible]);

  const floating = useFloating({
    placement: isMobile ? 'top' : 'left',
    whileElementsMounted: autoUpdate,
    middleware: [flip(), offset(0), shift()],
  });

  const outsideClickRef = useClickOutsideAlt<HTMLDivElement>(hideDescription, {
    enabled: descriptionVisible,
  });

  return {
    descriptionVisible,
    toggleDescription,
    hideDescription,
    showDescription,
    popoverStyles: floating.floatingStyles,
    anchorRef: floating.refs.setReference,
    popoverRef,
    setPopoverRef: useCallback(
      (node: HTMLDivElement) => {
        popoverRef.current = node;
        floating.refs.setFloating(node);
      },
      [popoverRef, floating.refs],
    ),
    outsideClickRef,
  };
}
